/**
 * Canonical types for the project feature.
 * Consuming sites map their API responses to these types.
 */

export const Site = {
  CustomerSuccess: "customer-success",
  CustomerExperience: "customer-experience",
} as const;

export type SiteContext = (typeof Site)[keyof typeof Site];

export type DateValue =
  | Date
  | string
  | { year: number; month: number; day: number }
  | null;

export interface ProjectIndividual {
  projectId: number;
  project: ProjectInfo | null;
  activePhase: number | null;
  tasks: ProjectTask[];
  orphanTasks: ProjectTask[];
  attachedResources: Resource[];
  errorMessage: string | null;
}

export interface LinkedItProject {
  itProjectId: string;
  title: string;
  crmAccountId: string;
}

export interface ProjectInfo {
  projectID: number;
  projectName: string;
  status: number | null;
  accountID: number | null;
  accountName: string | null;
  axAccountNumber: string | null;
  contractID: number | null;
  startDateTime: DateValue;
  endDateTime: DateValue;
  projectLeadFirstName: string | null;
  projectLeadLastName: string | null;
  projectLeadEmail: string | null;
  accountExecutive: string | null;
}

export interface PhaseWithTasks {
  phase: Phase | null;
  tasks: ProjectTask[];
  childPhases: PhaseWithTasks[];
}

export interface Phase {
  id: number;
  title: string | null;
}

export interface ProjectTask {
  id: number;
  title: string | null;
  status: number | null;
  assignedResourceID: number | null;
  assignedResourceName: string | null;
  completedByResourceID: number | null;
  phase: string | null;
  isVisibleInClientPortal: boolean | null;
  startDateTime: DateValue;
  endDateTime: DateValue;
  completedDateTime: DateValue;
  progress: number | null;
}

export interface Resource {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

export interface ProjectDetail {
  projectTasksGroups: ProjectTaskGroup[];
}

export interface ProjectTaskGroup {
  title: string | null;
  projectTasks: ProjectTask[];
  completedTasks?: number | null;
  totalTasks?: number | null;
  projectStartDate?: DateValue;
  projectEndDate?: DateValue;
}

export interface Contract {
  id: number;
  contractName: string | null;
}

export interface PicklistItem {
  entityType: string;
  fieldName: string;
  value: number | string;
  label: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface IncludeExcludeFilters {
  assignee?: string[] | null;
  status?: string[] | null;
  phase?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface ProjectDetailParams {
  projectId: number;
  includeFilters?: IncludeExcludeFilters | null;
  excludeFilters?: IncludeExcludeFilters | null;
  sortBy?: string | null;
  groupBy?: string | null;
}
